import { tidyShareText } from '../links/shareTidy'
import {
  extractUrls,
  isOpaqueUrlPathPart,
  isUsableUrlTitle,
  removeKnownTrackingParameters,
  urlDisplayTitle,
  legacyPathTitle,
  type UrlEntity,
} from '../domain/note'

const INLINE_URL_LINK_PATTERN = /(!?)\[((?:\\.|[^\]\\\n])*)\]\((https?:\/\/[^\s<>()\[\]{}"']+)(\s+["'][^"']*["'])?\)/gi
// Accept escaped Markdown punctuation in labels without reading adjacent prose.
const STANDALONE_URL_LINK_PATTERN = /^\[((?:\\.|[^\]\\\n])+)\]\((https?:\/\/[^\s<>()\[\]{}"']+)(\s+["'][^"']*["'])?\)$/i
const AUTOLINK_PATTERN = /<(https?:\/\/[^\s<>()\[\]{}"']+)>/gi
const RAW_URL_PATTERN = /https?:\/\/[^\s<>()\[\]{}"']+/gi
const FENCED_CODE_PATTERN = /(?:^|\n)(`{3,}|~{3,})[^\n]*\n[\s\S]*?\n\1(?=\n|$)/g
const INLINE_CODE_PATTERN = /(`+)[^`\n]*\1/g
const PROTECTED_URL_MARKER = '\uE100'

function markdownLabel(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/([*_`\[\]])/g, '\\$1')
    .replace(/\s+/g, ' ')
    .trim()
}

function urlKey(url: UrlEntity): string {
  return removeKnownTrackingParameters(url.canonicalUrl ?? url.originalUrl, true)
}

/** Adds a secure default scheme, then accepts only a valid HTTP(S) destination. */
export function normalizeSubmittedWebUrl(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed || /\s/.test(trimmed)) return null
  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
  try {
    const parsed = new URL(candidate)
    if (!parsed.hostname || (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')) return null
    return removeKnownTrackingParameters(parsed.href)
  } catch {
    return null
  }
}

function entityForUrl(value: string, metadata: readonly UrlEntity[]): UrlEntity | null {
  const extracted = extractUrls(value)[0]
  if (!extracted) return null
  const key = urlKey(extracted)
  return metadata.find((url) => urlKey(url) === key) ?? extracted
}

function fallbackLabelForUrl(value: string): string | null {
  const extracted = extractUrls(value)[0]
  return extracted ? urlDisplayTitle(extracted) : null
}

function legacyOpaquePathLabel(value: string): string | null {
  try {
    const part = new URL(value).pathname.split('/').filter(Boolean).at(-1)
    if (!part || !isOpaqueUrlPathPart(part)) return null
    return decodeURIComponent(part)
      .replace(/[-_+]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  } catch {
    return null
  }
}

function shouldReplaceLabel(label: string, rawUrl: string, cleanedUrl: string): boolean {
  const normalizedLabel = label.trim()
  const fallback = fallbackLabelForUrl(cleanedUrl)
  const legacyOpaqueFallback = legacyOpaquePathLabel(cleanedUrl)
  return normalizedLabel === rawUrl
    || normalizedLabel === cleanedUrl
    || normalizedLabel === fallback
    || normalizedLabel === legacyOpaqueFallback
    || normalizedLabel === legacyPathTitle(cleanedUrl)
}

/** Returns a richer label only when the current one was generated from the URL itself. */
export function healedAutomaticUrlLabel(
  label: string,
  rawUrl: string,
  metadata: readonly UrlEntity[],
): string | null {
  const cleanedUrl = removeKnownTrackingParameters(rawUrl)
  const entity = entityForUrl(cleanedUrl, metadata)
  const replacesLegacyOpaqueFallback = label.trim() === legacyOpaquePathLabel(cleanedUrl)
  if (
    !entity
    || (!isUsableUrlTitle(entity, entity.title) && !replacesLegacyOpaqueFallback)
    || !shouldReplaceLabel(label, rawUrl, cleanedUrl)
  ) return null
  const next = urlDisplayTitle(entity)
  return next === label.trim() ? null : next
}

function splitTrailingPunctuation(value: string): { url: string; trailing: string } {
  const trailing = value.match(/[.,;:!?]+$/)?.[0] ?? ''
  return {
    url: trailing ? value.slice(0, -trailing.length) : value,
    trailing,
  }
}

/**
 * Cleans every HTTP(S) destination and promotes visible raw URLs to Markdown links.
 * Existing authored link labels are retained; generated fallback labels can heal when
 * richer metadata is supplied later.
 */
export function formatNoteUrlsAsMarkdown(
  markdown: string,
  metadata: readonly UrlEntity[] = [],
): string {
  const protectedLinks: string[] = []
  const protect = (value: string) => {
    const index = protectedLinks.push(value) - 1
    return `${PROTECTED_URL_MARKER}${index}${PROTECTED_URL_MARKER}`
  }

  const withProtectedCode = markdown
    .replace(FENCED_CODE_PATTERN, protect)
    .replace(INLINE_CODE_PATTERN, protect)
  const withProtectedMarkdownLinks = withProtectedCode.replace(
    INLINE_URL_LINK_PATTERN,
    (_match, imagePrefix: string, label: string, rawUrl: string, title = '') => {
      const cleanedUrl = removeKnownTrackingParameters(rawUrl)
      if (imagePrefix) return protect(`![${label}](${cleanedUrl}${title})`)
      const healedLabel = healedAutomaticUrlLabel(label, rawUrl, metadata)
      const nextLabel = healedLabel ? markdownLabel(healedLabel) : label
      return protect(`[${nextLabel}](${cleanedUrl}${title})`)
    },
  )
  const withProtectedAutolinks = withProtectedMarkdownLinks.replace(
    AUTOLINK_PATTERN,
    (_match, rawUrl: string) => {
      const cleanedUrl = removeKnownTrackingParameters(rawUrl)
      const entity = entityForUrl(cleanedUrl, metadata)
      return protect(entity
        ? `[${markdownLabel(urlDisplayTitle(entity))}](${cleanedUrl})`
        : cleanedUrl)
    },
  )
  const formatted = withProtectedAutolinks.replace(RAW_URL_PATTERN, (match) => {
    const { url: rawUrl, trailing } = splitTrailingPunctuation(match)
    const cleanedUrl = removeKnownTrackingParameters(rawUrl)
    const entity = entityForUrl(cleanedUrl, metadata)
    return entity
      ? `${protect(`[${markdownLabel(urlDisplayTitle(entity))}](${cleanedUrl})`)}${trailing}`
      : `${cleanedUrl}${trailing}`
  })

  return formatted.replace(
    new RegExp(`${PROTECTED_URL_MARKER}(\\d+)${PROTECTED_URL_MARKER}`, 'g'),
    (_match, index: string) => protectedLinks[Number(index)] ?? '',
  )
}

/** The web share's url field is separate from its text, so tidy them together and take the text back out. */
function tidyWebShareText(text: string, url: string, title: string): string {
  if (!text || !url) return text
  const tidy = tidyShareText(`${text}\n${url}`, title)
  if (tidy.original === null) return text
  return tidy.body.split('\n').filter((line) => line.trim() !== url).join('\n').trim()
}

/** Builds one web-share note without repeating the source title as raw text. */
function buildWebShareBody(title: string, text: string, url: string, tidy: boolean): string {
  const cleanTitle = title.trim()
  const cleanText = tidy ? tidyWebShareText(text.trim(), url.trim(), cleanTitle) : text.trim()
  const cleanedUrl = removeKnownTrackingParameters(url.trim())
  const urlEntity = extractUrls(cleanedUrl)[0]
  if (!urlEntity) {
    return [...new Set([cleanTitle, cleanText]
      .filter(Boolean)
      .map((value) => formatNoteUrlsAsMarkdown(value)))].join('\n')
  }
  const sourceEntity = cleanTitle ? { ...urlEntity, title: cleanTitle } : urlEntity
  const textContainsUrl = extractUrls(cleanText).some((candidate) => urlKey(candidate) === urlKey(urlEntity))
  const formattedText = cleanText && cleanText !== cleanTitle
    ? formatNoteUrlsAsMarkdown(cleanText, [sourceEntity])
    : ''
  const link = urlEntity
    ? `[${markdownLabel(cleanTitle || urlDisplayTitle(urlEntity))}](${cleanedUrl})`
    : ''
  return [...new Set([
    cleanText === url.trim() ? '' : formattedText,
    textContainsUrl ? '' : link,
  ].filter(Boolean))].join('\n')
}

/** Builds one clean web-share note without repeating the source title as raw text. */
export function formatWebShareBody(title: string, text: string, url: string): string {
  return buildWebShareBody(title, text, url, true)
}

/** The tidied web-share body plus the untidied one, when tidying changed anything. */
export function webShareWithOriginal(title: string, text: string, url: string): { body: string; original: string | null } {
  const body = buildWebShareBody(title, text, url, true)
  const untidied = buildWebShareBody(title, text, url, false)
  return { body, original: untidied === body ? null : untidied }
}

/** Reads an explicit label only when the complete body is a single matching link. */
export function authoredUrlLabelInMarkdown(markdown: string, target: UrlEntity): string | null {
  const match = STANDALONE_URL_LINK_PATTERN.exec(markdown.trim())
  if (!match) return null
  const [, label = '', rawUrl = ''] = match
  const cleanedUrl = removeKnownTrackingParameters(rawUrl)
  const linkedEntity = extractUrls(cleanedUrl)[0]
  if (!linkedEntity || urlKey(linkedEntity) !== urlKey(target)
    || shouldReplaceLabel(label, rawUrl, cleanedUrl)) return null
  return label.replace(/\\([\\!*_`\[\]])/g, '$1').trim() || null
}

/**
 * Shared text from another app is machine-made prose, not Markdown the person wrote.
 * Providers often indent detail lines (for example under a "Details" label); after a
 * blank line four spaces or a tab would render as a code block. When a share carries a
 * link, flatten leading indentation so it stays readable text.
 */
export function flattenSharedLinkText(body: string): string {
  if (!/https?:\/\//i.test(body)) return body
  return body
    .split(/\r?\n/)
    .map((line) => line.replace(/^[\s\u00A0]+/, '').replace(/[\s\u00A0]+$/, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
}

/**
 * Promote a captured provider title only at share intake, with an explicit source
 * field. Never infer title provenance from ordinary pasted or previously saved prose.
 */
export function formatCapturedShareBody(rawBody: string, sourceTitle?: string): string {
  const body = flattenSharedLinkText(rawBody)
  const formatted = formatNoteUrlsAsMarkdown(body)
  const title = sourceTitle?.trim()
  if (!title || /[\r\n]/.test(title) || extractUrls(title).length > 0) return formatted
  const lines = body.trim().split(/\r?\n/).map((line) => line.trim())
  const linkLine = lines.length === 1 ? lines[0]
    : lines.length === 2 && lines.filter((line) => line === title).length === 1
      ? lines.find((line) => line !== title)
      : undefined
  if (!linkLine) return formatted
  const link = STANDALONE_URL_LINK_PATTERN.exec(formatNoteUrlsAsMarkdown(linkLine))
  if (!link) return formatted
  const [, label = '', rawUrl = '', linkTitle = ''] = link
  const cleanedUrl = removeKnownTrackingParameters(rawUrl)
  if (!shouldReplaceLabel(label, rawUrl, cleanedUrl)) return formatted
  try {
    const parsed = new URL(cleanedUrl)
    const parts = parsed.pathname.split('/').filter(Boolean)
    if (parsed.hostname.toLowerCase().replace(/^www\./, '') !== 'perplexity.ai'
      || parts.length !== 2 || parts[0] !== 'search' || !isOpaqueUrlPathPart(parts[1]!)) return formatted
  } catch { return formatted }
  return `[${markdownLabel(title)}](${cleanedUrl}${linkTitle})`
}

/**
 * The label a phone sends along with a shared screenshot or photo ("Screenshot (22 Oct 2026 10:07:38 am)", "Screenshot_20261022-100738.png",
 * "IMG_1234.jpg", "PXL_20261022_081500.jpg"): a file name, not the person's words, so it never becomes the note's text or title. Only a
 * name followed by nothing but a date, a time or a number counts; "Screenshot of the menu" stays.
 */
export function isSharedFileLabel(text: string): boolean {
  const line = text.trim()
  if (!line || line.includes('\n') || line.length > 80 || !/\d/.test(line)) return false
  const named = /^(?:screenshot|screen shot|whatsapp image|img|image|photo|pxl|dsc|signal)(?=[\s_\-(])/i.exec(line)
  if (!named) return false
  // What follows the name is only a date, a time or a number (and the file's extension).
  const rest = line.slice(named[0].length).replace(/\.(?:png|jpe?g|webp|heic|gif)$/i, '').replace(/\b(?:jan|feb|mar|apr|may|jun|jul|aug|sept?|oct|nov|dec)[a-z]*\b|\b(?:at|am|pm)\b/gi, '')
  return /^[\d\s_.:,\-()]+$/.test(rest)
}

/** A native share's note body with packaging trimmed, plus the untidied body when trimming changed it. */
export function capturedShare(rawBody: string, sourceTitle?: string): { body: string; original: string | null } {
  const tidy = tidyShareText(flattenSharedLinkText(rawBody), sourceTitle)
  const body = formatCapturedShareBody(tidy.body, sourceTitle)
  const untidied = tidy.original === null ? body : formatCapturedShareBody(rawBody, sourceTitle)
  return { body, original: untidied === body ? null : untidied }
}
